import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle, XCircle, ArrowRight, ShieldCheck, Info } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export function PaymentSuccess() {
  const loc = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState({ payment_status: "pending", polling: true });
  const [attempts, setAttempts] = useState(0);

  const params = new URLSearchParams(loc.search);
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setStatus({ ...data, polling: data.payment_status !== "paid" && data.payment_status !== "failed" });
        if (data.payment_status === "paid" || data.payment_status === "failed") return;
        if (attempts < 15) {
          setAttempts((a) => a + 1);
          setTimeout(poll, 2000);
        } else {
          setStatus((s) => ({ ...s, polling: false }));
        }
      } catch (_) {
        if (!cancelled) setStatus((s) => ({ ...s, polling: false, error: true }));
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const paid = status.payment_status === "paid";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 grid place-items-center p-6">
      <div className="max-w-md w-full text-center">
        <div className={`w-20 h-20 rounded-3xl grid place-items-center mx-auto mb-5 ${paid ? "bg-emerald-500/15 border border-emerald-500/30" : "bg-orange-500/15 border border-orange-500/30"}`}>
          {paid ? <CheckCircle size={40} weight="bold" className="text-emerald-400" /> : <ShieldCheck size={40} weight="duotone" className="text-orange-400" />}
        </div>
        <h1 className="text-3xl font-display font-black tracking-tight mb-3">
          {paid ? "You're subscribed." : status.polling ? "Confirming payment…" : "Payment status pending"}
        </h1>
        <p className="text-slate-400 mb-6">
          {paid
            ? `Your ${status.lookup_key?.replace("auditready_", "").replace("_", " ")} plan is active in TEST MODE. Enjoy AuditReady Sports.`
            : status.polling
            ? "Your payment is being confirmed. This usually takes a few seconds."
            : "We haven't received confirmation yet. It's safe to refresh or return to the dashboard."}
        </p>

        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 mb-6 flex gap-2 items-start text-left" data-testid="test-mode-notice">
          <Info size={16} weight="bold" className="text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-200">
            <span className="font-semibold">Test mode.</span> No real money was charged. Live billing will be enabled after the contest version is complete and tested.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <Button
            data-testid="payment-success-dashboard"
            onClick={() => navigate("/dashboard")}
            className="bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full h-11"
          >
            Go to Dashboard <ArrowRight size={14} weight="bold" className="ml-1.5" />
          </Button>
          <Link to="/pricing" className="text-xs text-slate-500 hover:text-orange-300 mt-2">Back to pricing</Link>
        </div>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 grid place-items-center p-6">
      <div className="max-w-md w-full text-center" data-testid="payment-cancel-page">
        <div className="w-20 h-20 rounded-3xl bg-slate-800 border border-white/10 grid place-items-center mx-auto mb-5">
          <XCircle size={40} weight="bold" className="text-slate-400" />
        </div>
        <h1 className="text-3xl font-display font-black tracking-tight mb-3">Checkout cancelled</h1>
        <p className="text-slate-400 mb-6">No worries — you weren't charged. Come back any time to pick a plan.</p>
        <div className="flex flex-col gap-2">
          <Link to="/pricing">
            <Button className="w-full bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full h-11">
              Back to Pricing
            </Button>
          </Link>
          <Link to="/dashboard" className="text-xs text-slate-500 hover:text-orange-300 mt-2">Return to dashboard</Link>
        </div>
      </div>
    </div>
  );
}
